import { isString } from "../utility/helper";
import { AvailableExperiment } from "../utility/constants/ab-testing";

export interface TLink {
  id: string;
  text: string;
  link: string;
  icon: string;
  description: string;
}

export interface Crest {
  id: string;
  text: string;
  link: string | null;
  icon: string;
  description: string;
}

export interface TButton {
  id: string;
  text: string;
  description: string;
}

export interface TMenu {
  id: string;
  text: string;
  link: string;
  children: TMenu[];
  experienceName?: AvailableExperiment;
  savedVehicles?: TLink | null;
}

export type TShopAuthStatus = "loggedOut" | "loggedIn" | null;

export interface TShopAdditionalContentMenu {
  id: string;
  link: string;
  text: string;
  iconUrl?: string;
  icon?: string;
  authStatus?: TShopAuthStatus;
}

export interface TShopMenu {
  id: string;
  text: string;
  link: string;
  cart?: string;
  wishlist?: TLink | null;
  children: TMenu[];
  additionalContent: TShopAdditionalContentMenu[];
  availableLocales: string[];
  availableLocalesOnlyForShop: string[];
  logoLinksToShopLocales: string[];
}

export interface ShopContentResponse {
  menuItems: TMenu[];
  additionalContent: TShopAdditionalContentMenu[];
}

export interface TConnectMenu {
  id: string;
  text: string;
  link: string;
  cart?: string;
  children: TMenu[];
}

export interface TVehiclePurchaseMenu {
  id: string;
  text: string;
  link: string;
  children: TMenu[];
  savedVehicles?: TLink | null;
}

export interface TCountryRecommender {
  statement: string;
  region: string;
  language: string;
  change: TButton;
}

export interface Models {
  text: string;
  id: string;
  all: TLink;
  series: Series[];
  experienceName?: string;
}

export interface ModelBanner {
  id: string;
  name: string;
  image: string;
  link: string;
  tags?: string[];
  modelType: string;
  engineType: string;
  addGradient?: boolean;
}

export interface Series {
  name: string;
  id: string;
  all: TLink;
  image: string;
  links: TLink[];
  tags: Tag[];
  blackEdition?: ModelBanner;
  bodyTypes: BodyType[];
  modelCompareImage?: string;
  modelBanners?: ModelBanner[];
}

export interface ModelImages {
  sideShot: {
    src: string;
    alt: string;
    height: number;
    width: number;
  };
  frontShot: {
    src: string;
    alt: string;
    height: number;
    width: number;
  };
}

export interface BodyType {
  id: string;
  name: string;
  modelType: string;
  description: string;
  images: ModelImages;
  link: string;
  tags: Tag[];
  modelsAmount: number;
  topTag?: {
    validUntil: string;
    text: string;
  };
}

export interface Tag {
  text: string;
  icon: string;
  engineType?: string;
}

export interface Dealer {
  id: string;
  text: string;
  numberOfDealers: number;
  search: {
    id: string;
    text: string;
    form: {
      inputPlaceholder: string;
      inputDescription: string;
      radiusLabel: string;
    };
    clear: TButton;
    or: string;
    geolocation: TButton;
    dropdown: {
      province: string;
      city: string;
      selectAProvince: string;
      selectACity: string;
    };
    load: string;
    map: TLink | null;
    outboundLink: TLink | null;
  };
  details: {
    id: string;
    finder: TLink | null;
    testDrive: TLink | null;
    backToSearch: string;
    directions: string;
    homepage: string;
    lessButton: string;
    moreButton: string;
    contact: string;
  };
  openingHours: OpeningHours | null;
}

export interface Level0 {
  [key: string]: string;
}

export interface CountrySelector {
  text: string | null;
  ariaLabel: string | null;
}

export interface Account {
  id: string;
  text: string;
  link: string;
  loggedInFallback: string; // fallback if user has no name
  login: TLink;
  loginText: string;
  logout: TLink;
  messages: TLink;
  more: TLink | null;
  connect: TLink | null;
  links: TLink[];
  savedSearches: TLink | null;
  savedVehicles: TLink | null;
  onlineOrderHistory: TLink | null;
  experienceName?: AvailableExperiment;
}

export interface Errors {
  wrong: string;
  retry: string;
  noResults: string;
  noInput: string;
  invalidInput: string;
  geolocationDenied: string;
}

export interface Content {
  menu: TButton;
  crest: TLink | null;
  back: TButton;
  close: TButton;
  accessibilityStatement: TLink | null;
  countryRecommender: TCountryRecommender;
  contextualButtons: {
    [buttonName: string]: TButton;
  };
  contextualLinks: {
    [linkName: string]: TLink;
  };
  electricOwnership: TMenu | null;
  models: Models | null;
  dealer: Dealer;
  account: Account | null;
  countrySelector: CountrySelector | null;
  vehiclePurchase: TMenu;
  shop: TShopMenu | null;
  externalShop: TLink | null;
  experience: TMenu | null;
  services: TMenu | null;
  legacyMore: TMenu;
  legacyShop: TMenu;
  error: Errors;
  abExperiences?: ContentExperimentDefinition[];
  level0: Level0;
  connect: TConnectMenu | null;
  scrollingIndicator: string;
}

export interface Target {
  attribute: string;
  inverted: string;
  type: string;
  value: string;
}

export interface VariationDefinition {
  name: string;
  percentage: number;
  variationMenuItems: VariationMenuItem[];
}

export interface VariationMenuItem {
  section: string;
  children: TMenu[] | TLink[];
}

export type ABVariation = VariationDefinition;

export interface ContentExperimentDefinition {
  name: string;
  percentage: number;
  includedTargets: Target[];
  excludedTargets: Target[];
  variations: VariationDefinition[];
}

export interface OpeningHours {
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;
  daily_lunch: string;
  timeFormat?: number;
  startOfWeek?: number;
}

const EMPTY_LINK: TLink = {
  id: "",
  text: "",
  link: "",
  icon: "",
  description: "",
};

const EMPTY_BUTTON: TButton = { id: "", text: "", description: "" };

const EMPTY_COUNTRY_RECOMMENDER: TCountryRecommender = {
  statement: "",
  region: "",
  language: "",
  change: EMPTY_BUTTON,
};

const EMPTY_MODELS: Models = {
  text: "",
  id: "",
  all: {
    id: "",
    text: "",
    link: "",
    icon: "",
    description: "",
  },
  series: [],
};

const EMPTY_MENU: TMenu = {
  id: "",
  text: "",
  link: "",
  children: [],
};

export const EMPTY_SHOP_MENU: TShopMenu = {
  cart: "",
  wishlist: null,
  additionalContent: [],
  id: "",
  text: "",
  link: "",
  children: [],
  availableLocales: [],
  availableLocalesOnlyForShop: [],
  logoLinksToShopLocales: [],
};

const EMPTY_COUNTRY_SELECTOR: CountrySelector = {
  text: null,
  ariaLabel: null,
};

const EMPTY_ACCOUNT: Account = {
  id: "",
  text: "",
  link: "",
  loggedInFallback: "", // fallback if user has no name
  login: EMPTY_LINK,
  loginText: "",
  logout: EMPTY_LINK,
  messages: EMPTY_LINK,
  connect: null,
  more: null,
  links: [],
  savedSearches: null,
  savedVehicles: null,
  onlineOrderHistory: null,
};

const EMPTY_DEALER: Dealer = {
  id: "",
  text: "",
  numberOfDealers: 42,
  search: {
    id: "",
    text: "",
    form: {
      inputPlaceholder: "",
      inputDescription: "",
      radiusLabel: "",
    },
    clear: EMPTY_BUTTON,
    or: "",
    geolocation: EMPTY_BUTTON,
    dropdown: {
      province: "",
      city: "",
      selectAProvince: "",
      selectACity: "",
    },
    load: "",
    map: EMPTY_LINK,
    outboundLink: EMPTY_LINK,
  },
  details: {
    id: "",
    finder: EMPTY_LINK,
    testDrive: EMPTY_LINK,
    backToSearch: "",
    directions: "",
    homepage: "",
    lessButton: "",
    moreButton: "",
    contact: "",
  },
  openingHours: null,
};

const EMPTY_ERRORS: Errors = {
  wrong: "",
  retry: "",
  noResults: "",
  noInput: "",
  invalidInput: "",
  geolocationDenied: "",
};

export const EMPTY_CONNECT_MENU: TConnectMenu = {
  cart: "",
  id: "",
  text: "",
  link: "",
  children: [],
};

export const EMPTY_CONTENT: Content = {
  menu: EMPTY_BUTTON,
  crest: EMPTY_LINK,
  back: EMPTY_BUTTON,
  close: EMPTY_BUTTON,
  accessibilityStatement: EMPTY_LINK,
  countryRecommender: EMPTY_COUNTRY_RECOMMENDER,
  contextualButtons: {},
  contextualLinks: {},
  models: EMPTY_MODELS,
  dealer: EMPTY_DEALER,
  account: EMPTY_ACCOUNT,
  countrySelector: EMPTY_COUNTRY_SELECTOR,
  vehiclePurchase: EMPTY_MENU,
  electricOwnership: EMPTY_MENU,
  shop: EMPTY_SHOP_MENU,
  externalShop: EMPTY_LINK,
  experience: EMPTY_MENU,
  services: EMPTY_MENU,
  error: EMPTY_ERRORS,
  legacyMore: EMPTY_MENU,
  legacyShop: EMPTY_MENU,
  level0: {},
  connect: EMPTY_CONNECT_MENU,
  scrollingIndicator: "",
} as const;

export function findMenuItemById(menu: TMenu | null, id: string): TMenu | null {
  if (!isString(id)) {
    return null;
  }

  if (menu !== null) {
    if (menu.id === id) {
      return menu;
    }

    if (!Array.isArray(menu.children)) {
      return null;
    }
    // eslint-disable-next-line
    for (const child of menu.children) {
      const foundChild = findMenuItemById(child, id);

      if (foundChild !== null) {
        return foundChild;
      }
    }
  }

  return null;
}
